{
  "common": {
    "lastUpdated": "Last updated: {{date}}"
  },
  "privacy": {
    "title": "Privacy Notice",
    "updated": "7 August 2026",
    "intro": "This notice explains how <strong>{{tradingName}}</strong> (&ldquo;we&rdquo;, &ldquo;us&rdquo;) collects and uses personal data. We act as the <strong>data controller</strong> for the personal data described below, meaning we decide why and how it is processed.",
    "dataCollected": {
      "heading": "Data we collect and why",
      "items": [
        "<strong>Parent account data</strong> (name, email, phone number, country, time zone) &mdash; to create and administer your account, deliver lessons and contact you. Legal basis: performance of our contract with you.",
        "<strong>Child profile data</strong> (first name, age or year group, learning level and goals) &mdash; to place your child in the right cohort and personalise teaching. Legal basis: contract performance, with your consent as parent or guardian.",
        "<strong>Learning content</strong> (homework audio recordings, worksheets, tutor feedback, progress notes) &mdash; to teach, assess and report on progress. Legal basis: contract performance.",
        "<strong>Support messages and assessment enquiries</strong> &mdash; to answer questions and arrange lessons. Legal basis: contract performance and our legitimate interest in responding to enquiries.",
        "<strong>Usage, device and log data</strong> (IP address, device and browser identifiers, pages viewed, sign-in events) &mdash; for security, fraud prevention, troubleshooting and improving the Service. Legal basis: legitimate interests.",
        "<strong>Marketing preferences</strong> &mdash; to send updates you have asked for. Legal basis: consent, which you may withdraw at any time."
      ],
      "noChildData": "We do not knowingly collect data directly from children; accounts are created and managed by a parent or guardian."
    },
    "shareWith": {
      "heading": "Who we share data with",
      "items": [
        "<strong>Service providers / subprocessors</strong> &mdash; hosting, database, video lesson, email delivery and analytics providers acting on our instructions.",
        "<strong>Stripe</strong>, our payment processor, for checkout, subscription management, payments, tax calculation and invoicing.",
        "<strong>Professional advisers</strong> &mdash; legal and accounting, where necessary.",
        "<strong>Authorities</strong> &mdash; where required by law or to protect our rights."
      ],
      "noSell": "We do not sell personal data."
    },
    "transfers": {
      "heading": "International transfers",
      "body": "Our providers may process data outside your country, including outside the UK/EEA. Where that happens we rely on appropriate safeguards such as adequacy decisions or Standard Contractual Clauses."
    },
    "retention": {
      "heading": "Retention",
      "body": "We keep account and learning data for as long as your account is active and for up to 24 months afterwards, so progress reports remain available if you return. Records we must keep for legal, tax or accounting reasons are kept for the period required by law. Data is then deleted or anonymised."
    },
    "rights": {
      "heading": "Your rights",
      "body": "Subject to applicable law you may request access to your data, rectification, erasure, restriction of processing, portability, and object to processing based on legitimate interests. You may withdraw consent at any time. Send us a message via <0>Contact Us</0> and we will respond within one month. If you are in the UK or EEA you also have the right to complain to your local supervisory authority."
    },
    "security": {
      "heading": "Security",
      "body": "We use appropriate technical and organisational measures, including encryption in transit, access controls, row-level database security and private storage for homework uploads, so that only you and your child&rsquo;s assigned teacher can access submitted work."
    },
    "cookies": {
      "heading": "Cookies",
      "body": "We use essential cookies and local storage to keep you signed in and to secure the Service. We may use limited analytics cookies to understand how the site is used. You can manage or block cookies in your browser settings; blocking essential cookies will prevent sign-in from working."
    },
    "contact": {
      "heading": "Contact",
      "body": "Privacy questions: <0>Contact Us</0>."
    }
  },
  "terms": {
    "title": "Terms & Conditions",
    "updated": "7 August 2026",
    "intro": "These Terms &amp; Conditions govern your use of the {{tradingName}} website and our live online English tuition services for children aged 4&ndash;12 (the &ldquo;Service&rdquo;). The Service is provided by <strong>{{legalName}}</strong>{{tradingSuffix}} (&ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account, booking an assessment or purchasing a plan, you are entering into an agreement with us.",
    "tradingSuffix": ", trading as {{tradingName}}",
    "sections": {
      "acceptance": {
        "heading": "1. Acceptance",
        "body": "By accessing or continuing to use the Service you agree to these terms. If you are purchasing on behalf of an organisation, you confirm you have authority to bind it. If you are purchasing as an individual, you confirm you are of legal age and are the parent or legal guardian of the child enrolled."
      },
      "service": {
        "heading": "2. The Service",
        "body": "We provide live, scheduled online English lessons delivered in small cohorts or one-to-one, together with learning materials, homework review and progress reporting. Lesson times, cohort composition and teaching staff may change from time to time. We may update or improve the curriculum without notice, provided the overall standard of the Service is maintained."
      },
      "accounts": {
        "heading": "3. Accounts and accuracy",
        "items": [
          "You must provide accurate account and child information and keep it up to date.",
          "You are responsible for keeping your login credentials confidential and for all activity under your account, including your child&rsquo;s use of it.",
          "Accounts are for a single family and may not be shared or resold."
        ]
      },
      "acceptableUse": {
        "heading": "4. Acceptable use",
        "intro": "You must not misuse the Service. In particular, you must not:",
        "items": [
          "use the Service for any unlawful purpose, or in breach of any applicable regulation;",
          "engage in fraud, spam, harassment, abuse or disruptive behaviour in lessons;",
          "infringe our or a third party&rsquo;s intellectual property, including by recording, copying, redistributing or reselling lesson content or materials;",
          "interfere with the security or integrity of the Service, including by introducing malware, probing or scanning systems, or scraping the site."
        ]
      },
      "licence": {
        "heading": "5. Licence and restrictions",
        "body": "We grant you a limited, non-exclusive, non-transferable right to access the Service and the materials included in the plan you have selected, for personal, non-commercial family use. You may not reverse engineer, resell, sublicense or circumvent any technical limits of the Service."
      },
      "ip": {
        "heading": "6. Intellectual property",
        "body": "We retain all ownership of the Service and its intellectual property, including the curriculum, worksheets, recordings, software, branding and documentation. Work your child submits (such as homework audio and worksheets) remains yours; you grant us a limited licence to host and process it solely to deliver and improve the Service for you."
      },
      "serviceLevel": {
        "heading": "7. Service level",
        "body": "We aim to deliver lessons reliably, but we do not guarantee that the Service will be uninterrupted, timely, secure or error-free. To the fullest extent permitted by law we disclaim all implied warranties, including merchantability and fitness for a particular purpose. Learning outcomes depend on many factors and cannot be promised."
      },
      "payments": {
        "heading": "8. Payments, billing and subscriptions",
        "body1": "Plans are billed monthly in advance in US dollars unless stated otherwise at checkout, and renew automatically until cancelled. You can cancel at any time from your parent portal or via the billing portal; access continues until the end of the period you have paid for.",
        "body2": "Payments are processed securely by our payment provider, Stripe. We are the seller of record for all orders and remain responsible for the Service, billing queries and refunds. Card details are handled by Stripe and never stored on our systems. Applicable taxes are calculated and shown at checkout. See our <0>Refund Policy</0> for how refunds work."
      },
      "suspension": {
        "heading": "9. Suspension and termination",
        "body": "We may suspend or terminate access where there is a material breach of these terms, non-payment, a security or fraud risk, or repeated or serious policy violations. Where practicable we will notify you first and give you an opportunity to resolve the issue. On termination your access ends; you may request an export of your child&rsquo;s submitted work within 30 days, after which it may be deleted."
      },
      "liability": {
        "heading": "10. Liability",
        "body": "To the fullest extent permitted by law, our aggregate liability arising out of or in connection with the Service is limited to the fees you paid in the 12 months preceding the claim. We exclude liability for indirect, consequential or special damages, including loss of profits, data or goodwill. Nothing in these terms limits liability for fraud, death or personal injury caused by negligence, or any other liability that cannot lawfully be excluded."
      },
      "indemnity": {
        "heading": "11. Indemnity",
        "body": "You agree to indemnify us against claims arising from content you or your child submit, unlawful use of the Service, or breach of these terms."
      },
      "changes": {
        "heading": "12. Changes to these terms",
        "body": "We may update these terms; we will post the revised version here and update the date above. Continued use after changes take effect constitutes acceptance."
      },
      "general": {
        "heading": "13. General",
        "body": "You may not assign your rights without our consent; we may assign ours in connection with a merger or acquisition. Neither party is liable for delays caused by events beyond its reasonable control. These terms are governed by the laws of the seller&rsquo;s jurisdiction, and the courts of that jurisdiction have exclusive jurisdiction over disputes."
      },
      "contact": {
        "heading": "14. Contact",
        "body": "Questions about these terms, billing or orders: <0>Contact Us</0>."
      }
    }
  },
  "refund": {
    "title": "Refund Policy",
    "updated": "7 August 2026",
    "intro": "We want every family to feel confident about their child&rsquo;s learning. <strong>{{legalName}}</strong> offers a <strong>{{refundDays}}-day money-back guarantee</strong> on monthly plans.",
    "window": {
      "heading": "Your refund window",
      "body": "If you are not satisfied with your purchase, you can request a full refund within {{refundDays}} days of your order date. This applies to change of mind as well as to problems with the Service."
    },
    "howTo": {
      "heading": "How to request a refund",
      "items": [
        "Send us a message via <0>Contact Us</0>, using the email address you used at checkout and telling us the plan and the order date.",
        "You can also manage or cancel your plan at any time from the billing portal in your parent portal."
      ]
    },
    "cancelling": {
      "heading": "Cancelling your plan",
      "body": "You can cancel at any time from your parent portal or through the billing portal. Cancelling stops future renewals; your child keeps access to lessons and materials until the end of the period you have already paid for."
    },
    "renewals": {
      "heading": "Renewals",
      "body": "If a renewal charge was unexpected, contact us within {{refundDays}} days of the charge and we will review it and refund where appropriate."
    },
    "processing": {
      "heading": "Processing time",
      "body": "Approved refunds are returned to the original payment method. Depending on your bank or card issuer, funds typically appear within 5&ndash;10 business days."
    }
  }
}